/**
 * FirefliesIntegrationSettings
 *
 * Settings card for Fireflies.ai transcript ingestion. Users connect a
 * Fireflies API key for polling and can optionally configure Fireflies
 * developer webhooks for near-real-time transcript delivery.
 * for near-real-time transcript delivery.
 */

import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Check,
  Clock,
  Copy,
  Flame,
  Key,
  Loader2,
  RefreshCw,
  Unplug,
} from 'lucide-react';
import { useFirefliesIntegration } from '@/hooks/useFirefliesIntegration';

export function FirefliesIntegrationSettings() {
  const {
    status,
    loadingStatus,
    saving,
    syncing,
    unassignedCount,
    connect,
    disconnect,
    syncNow,
  } = useFirefliesIntegration();

  const [apiKey, setApiKey] = useState('');
  const [copied, setCopied] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  const handleConnect = async () => {
    if (!apiKey.trim()) return;
    const success = await connect(apiKey.trim());
    if (success) setApiKey('');
  };

  const handleCopyWebhook = async () => {
    if (!status.webhookUrl) return;
    await navigator.clipboard.writeText(status.webhookUrl).catch(() => undefined);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDisconnect = async () => {
    await disconnect();
    setShowDisconnectConfirm(false);
  };

  if (loadingStatus) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Flame className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-sm">Fireflies.ai Meeting Intelligence</p>
              <p className="text-xs text-muted-foreground mt-1">
                Connect Fireflies to import transcripts, summaries, action items, topics, and call metrics.
                Meetings are classified to the right deal or portfolio company and portfolio commentary updates automatically.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border bg-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Flame className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-lg">
                {status.configured ? 'Fireflies.ai Connected' : 'Connect Fireflies.ai'}
              </CardTitle>
              <CardDescription>
                {status.configured
                  ? 'Transcripts sync automatically every 15 minutes; Fireflies webhooks can deliver faster'
                  : 'Enter your Fireflies API key to get started'
                }
              </CardDescription>
            </div>
            {status.configured && (
              <Badge
                variant="outline"
                className={status.enabled
                  ? 'text-green-600 border-green-300 bg-green-50'
                  : 'text-muted-foreground border-border'
                }
              >
                {status.enabled ? <><Check className="h-3 w-3 mr-1" /> Connected</> : 'Paused'}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {!status.configured && (
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
                  <Key className="h-3.5 w-3.5" />
                  Fireflies API Key
                </label>
                <div className="flex gap-2">
                  <Input
                    type="password"
                    placeholder="Paste your Fireflies API key"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleConnect()}
                    className="flex-1 font-mono"
                  />
                  <Button onClick={handleConnect} disabled={saving || !apiKey.trim()}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                    {saving ? 'Validating...' : 'Connect'}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  In Fireflies, open Settings → Developer settings / API keys and generate an API key.
                </p>
              </div>

              <div className="bg-muted/30 rounded-lg p-4 space-y-2">
                <p className="text-sm font-medium">How it works:</p>
                <ol className="text-xs text-muted-foreground space-y-1.5 list-decimal list-inside">
                  <li>We validate and encrypt your Fireflies API key</li>
                  <li>Every 15 minutes, we fetch your latest transcripts</li>
                  <li>For faster delivery, configure Fireflies developer webhooks after connecting</li>
                  <li>AI classifies each meeting to the right project or portfolio company</li>
                  <li>Assigned portfolio company meetings update Portfolio commentary automatically</li>
                </ol>
              </div>
            </div>
          )}

          {status.configured && (
            <>
              {status.lastError && (
                <div className="rounded-lg bg-destructive/10 p-3">
                  <p className="text-xs font-medium text-destructive">Sync Error</p>
                  <p className="text-xs text-destructive/80 mt-0.5">{status.lastError}</p>
                </div>
              )}

              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-muted/50 rounded-lg text-center">
                  <p className="text-2xl font-bold tabular-nums">{status.totalIngested || 0}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Total Imported</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg text-center">
                  <p className="text-2xl font-bold tabular-nums">{status.recentCount || 0}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Last 7 Days</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg text-center">
                  <p className={`text-2xl font-bold tabular-nums ${(status.unassignedCount || unassignedCount) > 0 ? 'text-amber-600' : ''}`}>
                    {status.unassignedCount || unassignedCount}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">Unassigned</p>
                </div>
              </div>

              {status.webhookUrl && (
                <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                  <p className="text-sm font-medium">Optional Fireflies webhook setup:</p>
                  <p className="text-xs text-muted-foreground">
                    In Fireflies Developer settings, add this HTTPS endpoint and use the documented
                    <code className="mx-1 rounded bg-muted px-1 py-0.5 text-[10px]">Transcription completed</code>
                    webhook event.
                  </p>
                  <div className="flex gap-2">
                    <div className="flex-1 px-3 py-2 bg-background/60 rounded-md border text-xs font-mono break-all">
                      {status.webhookUrl}
                    </div>
                    <Button variant="outline" size="sm" onClick={handleCopyWebhook}>
                      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              )}

              {status.lastSyncedAt && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  Last synced: {(status.lastSyncedAt as Date).toLocaleString()}
                </div>
              )}

              <div className="flex items-center gap-3 pt-4 border-t">
                <Button variant="outline" size="sm" onClick={syncNow} disabled={syncing}>
                  {syncing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  {syncing ? 'Syncing...' : 'Sync Now'}
                </Button>
                <div className="flex-1" />
                {showDisconnectConfirm ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Are you sure?</span>
                    <Button variant="destructive" size="sm" onClick={handleDisconnect} disabled={saving}>
                      {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Yes, Disconnect'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setShowDisconnectConfirm(false)}>
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowDisconnectConfirm(true)}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <Unplug className="h-4 w-4 mr-1" />
                    Disconnect
                  </Button>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
